import React, { Component } from "react";
import Header from "../components/header";
import AdminService from "../services/admin_service";
import { toast } from "react-toastify";

class AdminScreen extends Component {
    state = {
        users: [],
        stations: [],
        zones: [],
        userSearch: "",
        stationSearch: "",
        zoneSearch: "",
        newZone: "",
        editingZone: null,
        editingZoneName: "",
        newUser: { name: "", email: "", password: "", role: 2 },
        editingUser: null,
        editUser: { name: "", email: "", password: "", role: 2 },
        savingUser: false,
        rename: {},
        activeTab: "users",
        creatingUser: false,
        busyUserIds: {},
        busyVersionIds: {},
        busyStations: {},
        busyZones: {},
        selectedStationNames: [],
        bulkStationZone: "",
        updatingBulkStationZone: false,
        showCreateUserModal: false,
        editingStation: null,
        editingName: "",
        // NEW: station edit modal state
        showEditStationModal: false,
        editStationOriginal: null,
        editStationName: "",
        editStationZone: "",
        savingStation: false,
        downloadingFullBackup: false,
        fullBackupProgress: null
    };

    roleNames = { 0: "Super Admin", 1: "Admin", 2: "User" };

    roleBadge = {
        0: { bg: "#ede9fe", color: "#6d28d9", label: "Super Admin" },
        1: { bg: "#dbeafe", color: "#1d4ed8", label: "Admin" },
        2: { bg: "#e2e8f0", color: "#475569", label: "User" }
    };

    componentDidMount() {
        this.load();
    }

    load = async () => {
        try {
            const path = localStorage.getItem("inputsPath") || "";
            const [users, stations, zones] = await Promise.all([
                AdminService.getUsers(),
                AdminService.getStations(path),
                AdminService.getZones()
            ]);
            this.setState({
                users: users.data.users || [],
                stations: stations.data.stations || [],
                zones: zones.data.zones || []
            });
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to load admin console");
        }
    };

    downloadFullBackup = async () => {
        if (!window.confirm(
            "Create and download a full backup of the database and uploaded station files? The archive may be large."
        )) return;

        let writable;
        this.setState({
            downloadingFullBackup: true,
            fullBackupProgress: null
        });
        try {
            if (typeof window.showSaveFilePicker === "function") {
                const fileHandle = await window.showSaveFilePicker({
                    suggestedName: "design_automation_full_backup.zip",
                    types: [{
                        description: "ZIP archive",
                        accept: { "application/zip": [".zip"] }
                    }]
                });
                writable = await fileHandle.createWritable();
                const response = await AdminService.downloadFullBackupStream();
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || "Failed to create full backup");
                }
                if (!response.body) {
                    throw new Error("This browser cannot stream the backup to the selected file");
                }

                const reader = response.body.getReader();
                const total = Number(response.headers.get("Content-Length")) || null;
                let loaded = 0;
                let trailingBytes = new Uint8Array(0);
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    loaded += value.length;
                    const combined = new Uint8Array(trailingBytes.length + value.length);
                    combined.set(trailingBytes);
                    combined.set(value, trailingBytes.length);
                    const writeLength = Math.max(0, combined.length - 22);
                    if (writeLength) {
                        await writable.write(combined.subarray(0, writeLength));
                    }
                    trailingBytes = combined.slice(writeLength);
                    this.setState({
                        fullBackupProgress: {
                            loaded,
                            total,
                            percent: total
                                ? Math.min(100, Math.round((loaded / total) * 100))
                                : null
                        }
                    });
                }

                if (
                    trailingBytes.length !== 22
                    || trailingBytes[0] !== 0x50
                    || trailingBytes[1] !== 0x4b
                    || trailingBytes[2] !== 0x05
                    || trailingBytes[3] !== 0x06
                    || trailingBytes[20] !== 0
                    || trailingBytes[21] !== 0
                ) {
                    throw new Error("The backup stream ended unexpectedly. Check the server logs and try again.");
                }
                await writable.write(trailingBytes);
                await writable.close();
                writable = null;
                toast.success("Full backup saved");
                return;
            }

            const response = await AdminService.downloadFullBackup((event) => {
                this.setState({
                    fullBackupProgress: {
                        loaded: event.loaded,
                        total: event.total,
                        percent: event.total
                            ? Math.min(100, Math.round((event.loaded / event.total) * 100))
                            : null
                    }
                });
            });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = "design_automation_full_backup.zip";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            toast.success("Full backup downloaded");
        } catch (error) {
            if (writable) {
                await writable.abort().catch(() => {});
            }
            if (error.name !== "AbortError") {
                toast.error(error.response?.data?.message || error.message || "Failed to create full backup");
            }
        } finally {
            this.setState({
                downloadingFullBackup: false,
                fullBackupProgress: null
            });
        }
    };

    // ---------------- USERS ----------------
    openCreateUserModal = () => {
        this.setState({
            newUser: { name: "", email: "", password: "", role: 2 },
            showCreateUserModal: true
        });
    };

    closeCreateUserModal = () => {
        if (this.state.creatingUser) return;
        this.setState({ showCreateUserModal: false });
    };

    createUser = async (event) => {
        event.preventDefault();
        this.setState({ creatingUser: true });
        try {
            await AdminService.createUser(this.state.newUser);
            toast.success("User created");
            this.setState({
                newUser: { name: "", email: "", password: "", role: 2 },
                showCreateUserModal: false
            });
            this.load();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to create user");
        } finally {
            this.setState({ creatingUser: false });
        }
    };

    openEditUserModal = (user) => {
        this.setState({
            editingUser: user,
            editUser: { name: user.name, email: user.email, password: "", role: user.role }
        });
    };

    closeEditUserModal = () => {
        if (this.state.savingUser) return;
        this.setState({
            editingUser: null,
            editUser: { name: "", email: "", password: "", role: 2 }
        });
    };

    saveEditUser = async (event) => {
        event.preventDefault();
        const { editingUser, editUser } = this.state;
        if (!editingUser) return;

        const data = {
            name: editUser.name.trim(),
            email: editUser.email.trim(),
            role: editUser.role
        };
        if (editUser.password) data.password = editUser.password;

        this.setState((state) => ({
            savingUser: true,
            busyUserIds: { ...state.busyUserIds, [editingUser.id]: true }
        }));
        try {
            await AdminService.updateUser(editingUser.id, data);
            toast.success("User updated");
            this.setState({
                editingUser: null,
                editUser: { name: "", email: "", password: "", role: 2 }
            });
            this.load();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update user");
        } finally {
            this.setState((state) => {
                const next = { ...state.busyUserIds };
                delete next[editingUser.id];
                return {
                    savingUser: false,
                    busyUserIds: next
                };
            });
        }
    };

    toggleUser = async (user) => {
        this.setState((s) => ({ busyUserIds: { ...s.busyUserIds, [user.id]: true } }));
        try {
            await AdminService.updateUser(user.id, { is_active: !user.is_active });
            toast.success(user.is_active === false ? "User reactivated" : "User deactivated");
            this.load();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update user");
        } finally {
            this.setState((s) => {
                const next = { ...s.busyUserIds };
                delete next[user.id];
                return { busyUserIds: next };
            });
        }
    };

    // ---------------- STATIONS ----------------
    updateStationZone = async (station, zone) => {
        try {
            await AdminService.updateStationZone({ station_name: station.name, zone });
            this.setState((state) => ({
                stations: state.stations.map((item) =>
                    item.name === station.name ? { ...item, zone } : item
                )
            }));
            toast.success("Station zone updated");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update station zone");
        }
    };

    toggleStationSelection = (stationName, checked) => {
        this.setState((state) => ({
            selectedStationNames: checked
                ? [...new Set([...state.selectedStationNames, stationName])]
                : state.selectedStationNames.filter((name) => name !== stationName)
        }));
    };

    toggleVisibleStationSelection = (stationNames) => {
        this.setState((state) => {
            const selected = new Set(state.selectedStationNames);
            const allVisibleSelected = stationNames.every((name) => selected.has(name));
            stationNames.forEach((name) => {
                if (allVisibleSelected) selected.delete(name);
                else selected.add(name);
            });
            return { selectedStationNames: Array.from(selected) };
        });
    };

    updateSelectedStationsZone = async () => {
        const { selectedStationNames, bulkStationZone } = this.state;
        if (!selectedStationNames.length || !bulkStationZone) {
            toast.info("Select stations and a zone");
            return;
        }
        if (!window.confirm(
            `Update ${selectedStationNames.length} selected station(s) to zone "${bulkStationZone}"?`
        )) return;

        this.setState({ updatingBulkStationZone: true });
        try {
            const response = await AdminService.updateStationsZone({
                station_names: selectedStationNames,
                zone: bulkStationZone
            });
            const updatedNames = new Set(response.data.station_names);
            this.setState((state) => ({
                stations: state.stations.map((station) =>
                    updatedNames.has(station.name)
                        ? { ...station, zone: response.data.zone }
                        : station
                ),
                selectedStationNames: [],
                bulkStationZone: ""
            }));
            toast.success(
                `Updated ${response.data.updated_count} station(s) to ${response.data.zone}`
            );
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update station zones");
        } finally {
            this.setState({ updatingBulkStationZone: false });
        }
    };

    // ---- Station edit modal ----
    openEditStationModal = (station) => {
        this.setState({
            showEditStationModal: true,
            editStationOriginal: station,
            editStationName: station.name,
            editStationZone: station.zone || "SER"
        });
    };

    closeEditStationModal = () => {
        if (this.state.savingStation) return;
        this.setState({
            showEditStationModal: false,
            editStationOriginal: null,
            editStationName: "",
            editStationZone: ""
        });
    };

    saveEditStationModal = async () => {
        const { editStationOriginal, editStationName, editStationZone } = this.state;
        if (!editStationOriginal) return;

        const trimmedName = (editStationName || "").trim();
        if (!trimmedName) {
            toast.info("Station name cannot be empty");
            return;
        }

        const nameChanged = trimmedName !== editStationOriginal.name;
        const zoneChanged = editStationZone !== (editStationOriginal.zone || "SER");

        if (!nameChanged && !zoneChanged) {
            this.closeEditStationModal();
            return;
        }

        this.setState({ savingStation: true });
        try {
            if (nameChanged) {
                await AdminService.renameStation({
                    old_name: editStationOriginal.name,
                    new_name: trimmedName,
                    path: localStorage.getItem("inputsPath")
                });
            }
            if (zoneChanged) {
                await AdminService.updateStationZone({
                    station_name: nameChanged ? trimmedName : editStationOriginal.name,
                    zone: editStationZone
                });
            }
            toast.success("Station updated");
            this.setState({
                showEditStationModal: false,
                editStationOriginal: null,
                editStationName: "",
                editStationZone: ""
            });
            this.load();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update station");
        } finally {
            this.setState({ savingStation: false });
        }
    };

    deleteStation = async (station) => {
        if (!window.confirm(`Delete station "${station.name}"? This cannot be undone.`)) return;
        this.setState((s) => ({ busyStations: { ...s.busyStations, [station.name]: true } }));
        try {
            await AdminService.deleteStation({
                station_name: station.name,
                path: localStorage.getItem("inputsPath")
            });
            toast.success("Station deleted");
            this.load();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to delete station");
        } finally {
            this.setState((s) => {
                const next = { ...s.busyStations };
                delete next[station.name];
                return { busyStations: next };
            });
        }
    };

    deleteStationVersion = async (station, version) => {
        const confirmed = window.confirm(
            `Permanently delete version ${version.version} for ${station.name}? This removes its files from Input_Documents, Output_Documents, and Converted_Documents, along with related verification and revision records.`
        );
        if (!confirmed) return;

        this.setState((state) => ({
            busyVersionIds: { ...state.busyVersionIds, [version.id]: true }
        }));
        try {
            await AdminService.deleteStationVersion(
                version.id,
                localStorage.getItem("inputsPath")
            );
            toast.success(`Version ${version.version} deleted`);
            await this.load();
        } catch (error) {
            await this.load();
            toast.error(error.response?.data?.message || "Failed to delete version");
        } finally {
            this.setState((state) => {
                const next = { ...state.busyVersionIds };
                delete next[version.id];
                return { busyVersionIds: next };
            });
        }
    };

    // ---------------- ZONES ----------------
    createZone = async (event) => {
        event.preventDefault();
        const name = this.state.newZone.trim();
        if (!name) return;
        try {
            await AdminService.createZone({ name });
            this.setState({ newZone: "" });
            this.load();
            toast.success("Zone created");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to create zone");
        }
    };

    startEditZone = (zone) => {
        this.setState({ editingZone: zone.id, editingZoneName: zone.name });
    };

    cancelEditZone = () => {
        this.setState({ editingZone: null, editingZoneName: "" });
    };

    updateZone = async (zone) => {
        const name = this.state.editingZoneName.trim();
        if (!name) return;
        this.setState((s) => ({ busyZones: { ...s.busyZones, [zone.id]: true } }));
        try {
            await AdminService.updateZone(zone.id, { name });
            this.setState({ editingZone: null, editingZoneName: "" });
            this.load();
            toast.success("Zone updated");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update zone");
        } finally {
            this.setState((s) => {
                const next = { ...s.busyZones };
                delete next[zone.id];
                return { busyZones: next };
            });
        }
    };

    deleteZone = async (zone) => {
        if (!window.confirm(`Delete zone "${zone.name}"?`)) return;
        this.setState((s) => ({ busyZones: { ...s.busyZones, [zone.id]: true } }));
        try {
            await AdminService.deleteZone(zone.id);
            this.load();
            toast.success("Zone deleted");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to delete zone");
        } finally {
            this.setState((s) => {
                const next = { ...s.busyZones };
                delete next[zone.id];
                return { busyZones: next };
            });
        }
    };

    // ---------------- helpers ----------------
    getInitials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((n) => n[0].toUpperCase())
            .join("") || "?";

    renderSearchInput(value, onChange, placeholder) {
        return (
            <div className="mb-3">
                <input
                    type="search"
                    className="form-control rounded-3 border border-2"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                />
            </div>
        );
    }

    // ---------------- renderers ----------------
    renderTabs() {
        const { activeTab, users, stations, zones } = this.state;
        const tabs = [
            { id: "users", label: "Users", count: users.length, icon: "👥" },
            { id: "stations", label: "Stations", count: stations.length, icon: "🏢" },
            { id: "zones", label: "Zones", count: zones.length, icon: "📍" },
            { id: "developer-options", label: "Developer Options", icon: "🛠️" }
        ];
        const activeIndex = tabs.findIndex((t) => t.id === activeTab);

        return (
            <div
                className="position-relative d-inline-flex p-1 rounded-3 w-100 mb-3"
                style={{ background: "#e4e9f0", maxWidth: 620 }}
                role="tablist"
                aria-label="Admin sections"
            >
                <span
                    aria-hidden="true"
                    className="position-absolute rounded-3 border border-2"
                    style={{
                        top: 4,
                        bottom: 4,
                        left: 4,
                        width: `calc(${100 / tabs.length}% - 4px)`,
                        background: "#ffffff",
                        borderColor: "#aab6c4",
                        boxShadow: "0 2px 6px rgba(15,23,42,0.14)",
                        transition: "transform 300ms cubic-bezier(0.22,1,0.36,1)",
                        transform: `translateX(${activeIndex * 100}%)`
                    }}
                />
                {tabs.map((t) => {
                    const active = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => this.setState({ activeTab: t.id })}
                            className={`position-relative border-0 bg-transparent rounded-3 py-2 px-1 d-inline-flex align-items-center justify-content-center gap-1 fw-semibold ${active ? "text-dark" : "text-secondary"}`}
                            style={{
                                zIndex: 1,
                                flex: "1 1 0",
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                fontSize: 13.5,
                                transition: "color 200ms ease"
                            }}
                        >
                            <span aria-hidden="true">{t.icon}</span>
                            <span>{t.label}</span>
                            {t.count !== undefined && (
                                <span
                                    className={`badge rounded-pill fw-semibold ${active ? "text-white" : "text-dark"}`}
                                    style={{
                                        background: active ? "#193e6c" : "#cbd5e1",
                                        fontSize: 10.5,
                                        padding: "2px 7px",
                                        transition: "background 200ms ease, color 200ms ease"
                                    }}
                                >
                                    {t.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    renderUsersTab() {
        const { users, busyUserIds, userSearch } = this.state;
        const searchTerm = userSearch.trim().toLocaleLowerCase();
        const filteredUsers = users.filter((user) =>
            [user.name, user.email, this.roleBadge[user.role]?.label, user.is_active === false ? "inactive" : "active"]
                .some((value) => String(value || "").toLocaleLowerCase().includes(searchTerm))
        );

        return (
            <div className="tab-pane-anim">
                {this.renderSearchInput(
                    userSearch,
                    (value) => this.setState({ userSearch: value }),
                    "Search users by name, email, or role"
                )}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 fw-semibold" style={{ color: "#1e3a5f", fontSize: 13.5 }}>
                            All Users
                        </h6>
                        <span className="badge rounded-pill fw-semibold text-secondary" style={{ background: "#e4e9f0", fontSize: 11 }}>
                            {users.length} {users.length === 1 ? "user" : "users"}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn text-white fw-semibold rounded-3 d-inline-flex align-items-center gap-2 border-0 px-3 py-2"
                        onClick={this.openCreateUserModal}
                        style={{ fontSize: 13.5, background: "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)" }}
                    >
                        <span aria-hidden="true">＋</span>
                        Create New User
                    </button>
                </div>

                {filteredUsers.length === 0 ? (
                    <div className="text-center bg-white rounded-3 border border-2 py-4 px-3" style={{ borderColor: "#b8c2cf", borderStyle: "dashed" }}>
                        {users.length === 0 ? (
                            <>
                                <div className="fs-3 opacity-50" aria-hidden="true">👥</div>
                                <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No users yet</div>
                                <div className="text-secondary small mt-1">Click "Create New User" to add one</div>
                            </>
                        ) : (
                            <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No users match your search</div>
                        )}
                    </div>
                ) : (
                    <div className="row g-2">
                        {filteredUsers.map((user) => {
                            const role = this.roleBadge[user.role] || { bg: "#f1f5f9", color: "#475569", label: "Unknown" };
                            const inactive = user.is_active === false;
                            const busy = !!busyUserIds[user.id];
                            return (
                                <div key={user.id} className="col-12 col-md-6 col-lg-4">
                                    <div
                                        className="bg-white rounded-3 border border-2 h-100 d-flex flex-column p-3"
                                        style={{ borderColor: "#b8c2cf", borderLeft: `4px solid ${inactive ? "#94a3b8" : "#193e6c"}` }}
                                    >
                                        <div className="d-flex align-items-start gap-3 mb-2">
                                            <div
                                                className="d-flex align-items-center justify-content-center fw-semibold flex-shrink-0 rounded-circle"
                                                style={{ width: 40, height: 40, background: "#e4e9f0", color: "#193e6c", fontSize: 14 }}
                                                aria-hidden="true"
                                            >
                                                {this.getInitials(user.name)}
                                            </div>
                                            <div className="flex-grow-1 min-w-0">
                                                <div className="fw-semibold text-truncate" style={{ fontSize: 14, color: "#0f172a" }} title={user.name}>
                                                    {user.name}
                                                </div>
                                                <div className="text-muted text-truncate small" title={user.email}>
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                                            <span className="badge rounded-pill fw-semibold" style={{ background: role.bg, color: role.color, fontSize: 10.5 }}>
                                                {role.label}
                                            </span>
                                            {inactive && (
                                                <span className="badge rounded-pill fw-semibold bg-danger-subtle text-danger-emphasis" style={{ fontSize: 10.5 }}>
                                                    Inactive
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-auto d-flex justify-content-end gap-2 pt-2 border-top">
                                            <button
                                                type="button"
                                                className="btn btn-sm bg-white border-primary text-primary rounded-3"
                                                onClick={() => this.openEditUserModal(user)}
                                                disabled={busy}
                                                aria-label={`Edit ${user.name}`}
                                                title="Edit user"
                                                style={{ fontSize: 12, padding: "5px 12px", borderWidth: 2, opacity: busy ? 0.6 : 1 }}
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                className={`btn btn-sm fw-semibold rounded-3 bg-white ${inactive ? "border-success text-success" : "border-danger text-danger"}`}
                                                onClick={() => this.toggleUser(user)}
                                                disabled={busy}
                                                style={{ fontSize: 12, padding: "5px 12px", borderWidth: 2, opacity: busy ? 0.6 : 1 }}
                                            >
                                                {busy ? "..." : inactive ? "Reactivate" : "Deactivate"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    renderStationsTab() {
        const {
            stations,
            busyStations,
            busyVersionIds,
            stationSearch,
            selectedStationNames,
            bulkStationZone,
            updatingBulkStationZone,
            zones
        } = this.state;
        const searchTerm = stationSearch.trim().toLocaleLowerCase();
        const filteredStations = stations
            .filter((station) =>
                [station.name, station.zone, ...(station.versions || []).map((version) => version.version)]
                    .some((value) => String(value || "").toLocaleLowerCase().includes(searchTerm))
            )
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

        return (
            <div className="tab-pane-anim">
                {this.renderSearchInput(
                    stationSearch,
                    (value) => this.setState({ stationSearch: value }),
                    "Search stations by name, zone, or version"
                )}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 fw-semibold" style={{ color: "#1e3a5f", fontSize: 13.5 }}>
                            All Stations
                        </h6>
                        <span className="badge rounded-pill fw-semibold text-secondary" style={{ background: "#e4e9f0", fontSize: 11 }}>
                            {stations.length} {stations.length === 1 ? "station" : "stations"}
                        </span>
                    </div>
                </div>

                <section
                    className="bg-white rounded-3 border border-2 p-3 mb-3"
                    style={{ borderColor: "#b8c2cf" }}
                    aria-label="Bulk station zone update"
                >
                    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2">
                        <div className="me-lg-auto">
                            <div className="fw-semibold" style={{ color: "#1e3a5f", fontSize: 13.5 }}>
                                Update multiple station zones
                            </div>
                            <div className="text-muted small">
                                {selectedStationNames.length} station(s) selected
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => this.toggleVisibleStationSelection(
                                filteredStations.map((station) => station.name)
                            )}
                            disabled={!filteredStations.length || updatingBulkStationZone}
                        >
                            {filteredStations.length > 0 &&
                                filteredStations.every((station) =>
                                    selectedStationNames.includes(station.name)
                                )
                                ? "Deselect visible"
                                : "Select visible"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-3"
                            onClick={() => this.setState({ selectedStationNames: [] })}
                            disabled={!selectedStationNames.length || updatingBulkStationZone}
                        >
                            Clear selection
                        </button>
                        <select
                            className="form-select form-select-sm rounded-3"
                            value={bulkStationZone}
                            onChange={(event) => this.setState({ bulkStationZone: event.target.value })}
                            disabled={updatingBulkStationZone}
                            aria-label="Zone to apply to selected stations"
                            style={{ maxWidth: 200 }}
                        >
                            <option value="">Choose zone</option>
                            {zones.map((zone) => (
                                <option key={zone.id} value={zone.name}>{zone.name}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn btn-sm text-white fw-semibold rounded-3 border-0"
                            onClick={this.updateSelectedStationsZone}
                            disabled={
                                !selectedStationNames.length
                                || !bulkStationZone
                                || updatingBulkStationZone
                            }
                            style={{ background: "#193e6c" }}
                        >
                            {updatingBulkStationZone ? "Updating..." : "Update selected"}
                        </button>
                    </div>
                </section>

                {filteredStations.length === 0 ? (
                    <div className="text-center bg-white rounded-3 border border-2 py-4 px-3" style={{ borderColor: "#b8c2cf", borderStyle: "dashed" }}>
                        {stations.length === 0 ? (
                            <>
                                <div className="fs-3 opacity-50" aria-hidden="true">🏢</div>
                                <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No stations available</div>
                            </>
                        ) : (
                            <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No stations match your search</div>
                        )}
                    </div>
                ) : (
                    <div className="row g-2">
                        {filteredStations.map((station) => {
                            const busy = !!busyStations[station.name];
                            const zone = station.zone || "SER";
                            const selected = selectedStationNames.includes(station.name);
                            return (
                                <div key={station.name} className="col-12 col-md-6 col-lg-4">
                                    <div
                                        className="bg-white rounded-3 border border-2 h-100 d-flex flex-column p-3"
                                        style={{ borderColor: "#b8c2cf", borderLeft: "4px solid #193e6c" }}
                                    >
                                        <div className="d-flex align-items-start gap-2 mb-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input mt-1"
                                                checked={selected}
                                                onChange={(event) =>
                                                    this.toggleStationSelection(
                                                        station.name,
                                                        event.target.checked
                                                    )
                                                }
                                                aria-label={`Select ${station.name} for bulk zone update`}
                                            />
                                            <div className="flex-grow-1 min-w-0">
                                                <div className="fw-semibold text-truncate" style={{ fontSize: 14, color: "#0f172a" }} title={station.name}>
                                                    {station.name}
                                                </div>
                                                <div className="mt-1 d-flex flex-wrap gap-2">
                                                    <span className="badge rounded-pill fw-semibold text-secondary" style={{ background: "#e4e9f0", fontSize: 10.5 }}>
                                                        📍 {zone}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <div className="small fw-semibold text-secondary mb-1">Available versions</div>
                                            {station.versions?.length ? (
                                                <div className="d-flex flex-column gap-1">
                                                    {station.versions.map((version, index) => {
                                                        const versionBusy = !!busyVersionIds[version.id];
                                                        const latestVersion = index === 0;
                                                        const isInitialVersion = version.version === "2.0.0";
                                                        const canDeleteVersion =
                                                            latestVersion &&
                                                            station.versions.length > 1 &&
                                                            !isInitialVersion;
                                                        return (
                                                            <div
                                                                key={version.id}
                                                                className="d-flex align-items-center justify-content-between rounded-2 px-2 py-1"
                                                                style={{ background: "#f1f5f9", fontSize: 12 }}
                                                            >
                                                                <span className="fw-semibold text-dark">v{version.version}</span>
                                                                {canDeleteVersion ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger py-0 px-2"
                                                                        onClick={() => this.deleteStationVersion(station, version)}
                                                                        disabled={versionBusy}
                                                                        aria-label={`Delete latest version ${version.version} for ${station.name}`}
                                                                        title="Permanently delete latest version and related files"
                                                                        style={{ fontSize: 11, opacity: versionBusy ? 0.6 : 1 }}
                                                                    >
                                                                        {versionBusy ? "Deleting..." : "Delete"}
                                                                    </button>
                                                                ) : (
                                                                    <span className="small text-muted">
                                                                        {isInitialVersion
                                                                            ? "Initial version"
                                                                            : station.versions.length <= 1
                                                                                ? "Keep at least one version"
                                                                                : "Latest version only"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="small text-muted">No versions available</div>
                                            )}
                                        </div>

                                        <div className="mt-auto d-flex align-items-center justify-content-end gap-2 pt-2 border-top">
                                            <button
                                                type="button"
                                                className="btn btn-sm bg-white border-primary text-primary rounded-3 d-inline-flex align-items-center justify-content-center"
                                                onClick={() => this.openEditStationModal(station)}
                                                aria-label={`Edit ${station.name}`}
                                                title="Edit"
                                                style={{ width: 32, height: 32, borderWidth: 2 }}
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm bg-white border-danger text-danger rounded-3 d-inline-flex align-items-center justify-content-center"
                                                onClick={() => this.deleteStation(station)}
                                                disabled={busy}
                                                aria-label={`Delete ${station.name}`}
                                                title="Delete"
                                                style={{ width: 32, height: 32, borderWidth: 2, opacity: busy ? 0.6 : 1 }}
                                            >
                                                🗑
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    renderZonesTab() {
        const { zones, newZone, editingZone, editingZoneName, busyZones, zoneSearch } = this.state;
        const searchTerm = zoneSearch.trim().toLocaleLowerCase();
        const filteredZones = zones.filter((zone) =>
            zone.name.toLocaleLowerCase().includes(searchTerm)
        );

        return (
            <div className="tab-pane-anim">
                {this.renderSearchInput(
                    zoneSearch,
                    (value) => this.setState({ zoneSearch: value }),
                    "Search zones by name"
                )}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 fw-semibold" style={{ color: "#1e3a5f", fontSize: 13.5 }}>
                            All Zones
                        </h6>
                        <span className="badge rounded-pill fw-semibold text-secondary" style={{ background: "#e4e9f0", fontSize: 11 }}>
                            {zones.length} {zones.length === 1 ? "zone" : "zones"}
                        </span>
                    </div>
                </div>

                {/* Add zone card */}
                <div className="bg-white rounded-3 border border-2 mb-3 p-3" style={{ borderColor: "#b8c2cf" }}>
                    <div className="d-flex align-items-center mb-2">
                        <span className="d-inline-block rounded-circle me-2" style={{ width: 8, height: 8, background: "#193e6c" }} />
                        <strong className="text-uppercase fw-semibold" style={{ color: "#1e3a5f", fontSize: 12, letterSpacing: 0.4 }}>
                            Add New Zone
                        </strong>
                    </div>
                    <form className="d-flex gap-2" onSubmit={this.createZone}>
                        <input
                            className="form-control rounded-3 border border-2"
                            placeholder="Enter zone name"
                            value={newZone}
                            onChange={(event) => this.setState({ newZone: event.target.value })}
                            style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                        />
                        <button
                            type="submit"
                            className="btn text-white fw-semibold rounded-3 border-0 px-3"
                            disabled={!newZone.trim()}
                            style={{
                                fontSize: 13.5,
                                background: !newZone.trim()
                                    ? "#94a3b8"
                                    : "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)",
                                opacity: !newZone.trim() ? 0.75 : 1
                            }}
                        >
                            Add Zone
                        </button>
                    </form>
                </div>

                {/* Zones grid */}
                {filteredZones.length === 0 ? (
                    <div className="text-center bg-white rounded-3 border border-2 py-4 px-3" style={{ borderColor: "#b8c2cf", borderStyle: "dashed" }}>
                        {zones.length === 0 ? (
                            <>
                                <div className="fs-3 opacity-50" aria-hidden="true">📍</div>
                                <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No zones yet</div>
                                <div className="text-secondary small mt-1">Add a zone above to get started</div>
                            </>
                        ) : (
                            <div className="fw-semibold text-secondary" style={{ fontSize: 13.5 }}>No zones match your search</div>
                        )}
                    </div>
                ) : (
                    <div className="row g-2">
                        {filteredZones.map((zone) => {
                            const isEditing = editingZone === zone.id;
                            const busy = !!busyZones[zone.id];
                            return (
                                <div key={zone.id} className="col-12 col-md-6 col-lg-4">
                                    <div
                                        className="bg-white rounded-3 border border-2 h-100 d-flex flex-column p-3"
                                        style={{ borderColor: "#b8c2cf", borderLeft: "4px solid #193e6c" }}
                                    >
                                        <div className="flex-grow-1 min-w-0 mb-2">
                                            {isEditing ? (
                                                <input
                                                    className="form-control form-control-sm rounded-3 border border-2"
                                                    value={editingZoneName}
                                                    onChange={(e) => this.setState({ editingZoneName: e.target.value })}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") this.updateZone(zone);
                                                        if (e.key === "Escape") this.cancelEditZone();
                                                    }}
                                                    style={{ borderColor: "#aab6c4", fontSize: 13.5, boxShadow: "none" }}
                                                />
                                            ) : (
                                                <div className="fw-semibold text-truncate" style={{ fontSize: 14, color: "#0f172a" }} title={zone.name}>
                                                    {zone.name}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-auto d-flex align-items-center justify-content-end gap-2 pt-2 border-top">
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm fw-semibold rounded-3 bg-white border-success text-success"
                                                        onClick={() => this.updateZone(zone)}
                                                        disabled={busy || !editingZoneName.trim()}
                                                        style={{ fontSize: 12, padding: "5px 10px", borderWidth: 2, opacity: busy ? 0.6 : 1 }}
                                                    >
                                                        {busy ? "..." : "Save"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm fw-semibold rounded-3 bg-white border-secondary text-secondary"
                                                        onClick={this.cancelEditZone}
                                                        disabled={busy}
                                                        style={{ fontSize: 12, padding: "5px 10px", borderWidth: 2 }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm bg-white border-primary text-primary rounded-3 d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => this.startEditZone(zone)}
                                                        aria-label={`Edit ${zone.name}`}
                                                        title="Edit"
                                                        style={{ width: 32, height: 32, borderWidth: 2 }}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm bg-white border-danger text-danger rounded-3 d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => this.deleteZone(zone)}
                                                        disabled={busy}
                                                        aria-label={`Delete ${zone.name}`}
                                                        title="Delete"
                                                        style={{ width: 32, height: 32, borderWidth: 2, opacity: busy ? 0.6 : 1 }}
                                                    >
                                                        🗑
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    renderDeveloperOptionsTab() {
        const { downloadingFullBackup, fullBackupProgress } = this.state;
        const formatBytes = (bytes) => {
            if (!Number.isFinite(bytes) || bytes < 0) return "";
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        };

        return (
            <div className="tab-pane-anim">
                <section
                    className="bg-white rounded-3 border border-2 p-3"
                    style={{ borderColor: "#b8c2cf" }}
                    aria-label="Full system backup"
                >
                    <h6 className="mb-1 fw-semibold" style={{ color: "#1e3a5f" }}>
                        Full System Backup
                    </h6>
                    <p className="text-muted small mb-3">
                        Download a PostgreSQL database backup and the files under the configured
                        design automation data directory, including Input, Output, and Converted
                        Documents. The archive includes restore instructions for moving to another
                        server.
                    </p>
                    <button
                        type="button"
                        className="btn text-white fw-semibold rounded-3 border-0"
                        onClick={this.downloadFullBackup}
                        disabled={this.state.downloadingFullBackup}
                        style={{ background: "#193e6c" }}
                    >
                        {downloadingFullBackup
                            ? "Preparing Full Backup..."
                            : "Download Full Backup"}
                    </button>
                    {downloadingFullBackup && (
                        <div className="mt-3" aria-live="polite">
                            <div className="d-flex justify-content-between small text-muted mb-1">
                                <span>
                                    {fullBackupProgress
                                        ? "Downloading backup..."
                                        : "Preparing backup on server..."}
                                </span>
                                {fullBackupProgress?.percent !== null
                                    && fullBackupProgress?.percent !== undefined && (
                                        <span>
                                            {fullBackupProgress.percent}% (
                                            {formatBytes(fullBackupProgress.loaded)} /{" "}
                                            {formatBytes(fullBackupProgress.total)})
                                        </span>
                                    )}
                            </div>
                            <div
                                className="progress"
                                role="progressbar"
                                aria-label="Full backup download progress"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                aria-valuenow={fullBackupProgress?.percent ?? undefined}
                                style={{ height: 10 }}
                            >
                                <div
                                    className={`progress-bar${fullBackupProgress?.percent == null
                                        ? " progress-bar-striped progress-bar-animated"
                                        : ""}`}
                                    style={{
                                        width: fullBackupProgress?.percent == null
                                            ? "100%"
                                            : `${fullBackupProgress.percent}%`
                                    }}
                                />
                            </div>
                            {fullBackupProgress?.percent == null && (
                                <div className="small text-muted mt-1">
                                    {fullBackupProgress
                                        ? `${formatBytes(fullBackupProgress.loaded)} downloaded`
                                        : "Backup size will appear once the download starts."}
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>
        );
    }

    renderCreateUserModal() {
        const { showCreateUserModal, newUser, creatingUser } = this.state;
        if (!showCreateUserModal) return null;

        return (
            <div
                className="modal d-block"
                tabIndex="-1"
                role="dialog"
                onClick={this.closeCreateUserModal}
                style={{
                    background: "rgba(15, 23, 42, 0.55)",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)"
                }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: 520 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        className="modal-content border-0 overflow-hidden d-flex flex-column"
                        style={{ borderRadius: 16, boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)" }}
                    >
                        <div
                            className="modal-header border-0 d-flex align-items-center py-2 px-3 flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #193e6c 55%, #2a5a94 100%)", color: "white" }}
                        >
                            <div className="d-flex align-items-center gap-2 me-auto">
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.14)", fontSize: 14, flexShrink: 0 }}
                                    aria-hidden="true"
                                >
                                    👤
                                </div>
                                <h6 className="modal-title mb-0 fw-semibold" style={{ fontSize: 15 }}>
                                    Create New User
                                </h6>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                onClick={this.closeCreateUserModal}
                                aria-label="Close"
                                disabled={creatingUser}
                            />
                        </div>

                        <form onSubmit={this.createUser} autoComplete="off">
                            <div className="modal-body p-3 bg-white">
                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Name</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        placeholder=""
                                        type="text"
                                        name="new_user_full_name"
                                        autoComplete="off"
                                        value={newUser.name}
                                        onChange={(e) => this.setState({ newUser: { ...newUser, name: e.target.value } })}
                                        required
                                        autoFocus
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Email</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        placeholder=""
                                        type="text"
                                        name="new_user_email_addr"
                                        autoComplete="off"
                                        value={newUser.email}
                                        onChange={(e) => this.setState({ newUser: { ...newUser, email: e.target.value } })}
                                        required
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Password</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        placeholder=""
                                        type="password"
                                        name="new_user_pwd_field"
                                        autoComplete="new-password"
                                        value={newUser.password}
                                        onChange={(e) => this.setState({ newUser: { ...newUser, password: e.target.value } })}
                                        required
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>

                                <div className="mb-1">
                                    <label className="form-label text-muted small mb-1">Role</label>
                                    <select
                                        className="form-select rounded-3 border border-2"
                                        name="new_user_role_selector"
                                        autoComplete="off"
                                        value={newUser.role}
                                        onChange={(e) => this.setState({ newUser: { ...newUser, role: Number(e.target.value) } })}
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    >
                                        <option value="0">Super Admin</option>
                                        <option value="1">Admin</option>
                                        <option value="2">User</option>
                                    </select>
                                </div>
                            </div>

                            <div
                                className="modal-footer border-top border-2 bg-white py-2 px-3 d-flex justify-content-end gap-2"
                                style={{ borderColor: "#b8c2cf" }}
                            >
                                <button
                                    type="button"
                                    className="btn fw-semibold rounded-3 bg-light border border-2 text-secondary px-3 py-2"
                                    onClick={this.closeCreateUserModal}
                                    disabled={creatingUser}
                                    style={{ fontSize: 13.5, borderColor: "#b8c2cf" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn text-white fw-semibold rounded-3 border-0 px-3 py-2"
                                    disabled={creatingUser}
                                    style={{
                                        fontSize: 13.5,
                                        background: creatingUser
                                            ? "#94a3b8"
                                            : "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)",
                                        opacity: creatingUser ? 0.75 : 1
                                    }}
                                >
                                    {creatingUser ? "Creating..." : "Create User"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    renderEditUserModal() {
        const { editingUser, editUser, savingUser } = this.state;
        if (!editingUser) return null;

        return (
            <div
                className="modal d-block"
                tabIndex="-1"
                role="dialog"
                onClick={this.closeEditUserModal}
                style={{
                    background: "rgba(15, 23, 42, 0.55)",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)"
                }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: 520 }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div
                        className="modal-content border-0 overflow-hidden"
                        style={{ borderRadius: 16, boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)" }}
                    >
                        <div
                            className="modal-header border-0 d-flex align-items-center py-2 px-3"
                            style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #193e6c 55%, #2a5a94 100%)", color: "white" }}
                        >
                            <h6 className="modal-title mb-0 fw-semibold me-auto" style={{ fontSize: 15 }}>
                                Edit User
                            </h6>
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                onClick={this.closeEditUserModal}
                                aria-label="Close"
                                disabled={savingUser}
                            />
                        </div>

                        <form onSubmit={this.saveEditUser} autoComplete="off">
                            <div className="modal-body p-3 bg-white">
                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Name</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        type="text"
                                        value={editUser.name}
                                        onChange={(event) => this.setState({ editUser: { ...editUser, name: event.target.value } })}
                                        required
                                        autoFocus
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Email</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        type="email"
                                        value={editUser.email}
                                        onChange={(event) => this.setState({ editUser: { ...editUser, email: event.target.value } })}
                                        required
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label text-muted small mb-1">Role</label>
                                    <select
                                        className="form-select rounded-3 border border-2"
                                        value={editUser.role}
                                        onChange={(event) => this.setState({ editUser: { ...editUser, role: Number(event.target.value) } })}
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    >
                                        <option value="0">Super Admin</option>
                                        <option value="1">Admin</option>
                                        <option value="2">User</option>
                                    </select>
                                </div>
                                <div className="mb-1">
                                    <label className="form-label text-muted small mb-1">New Password (optional)</label>
                                    <input
                                        className="form-control rounded-3 border border-2"
                                        type="password"
                                        autoComplete="new-password"
                                        value={editUser.password}
                                        onChange={(event) => this.setState({ editUser: { ...editUser, password: event.target.value } })}
                                        style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                    />
                                </div>
                            </div>
                            <div
                                className="modal-footer border-top border-2 bg-white py-2 px-3 d-flex justify-content-end gap-2"
                                style={{ borderColor: "#b8c2cf" }}
                            >
                                <button
                                    type="button"
                                    className="btn fw-semibold rounded-3 bg-light border border-2 text-secondary px-3 py-2"
                                    onClick={this.closeEditUserModal}
                                    disabled={savingUser}
                                    style={{ fontSize: 13.5, borderColor: "#b8c2cf" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn text-white fw-semibold rounded-3 border-0 px-3 py-2"
                                    disabled={savingUser || !editUser.name.trim() || !editUser.email.trim()}
                                    style={{
                                        fontSize: 13.5,
                                        background: savingUser || !editUser.name.trim() || !editUser.email.trim()
                                            ? "#94a3b8"
                                            : "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)",
                                        opacity: savingUser ? 0.75 : 1
                                    }}
                                >
                                    {savingUser ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    renderEditStationModal() {
        const { showEditStationModal, editStationName, editStationZone, savingStation, zones } = this.state;
        if (!showEditStationModal) return null;

        const canSave = editStationName.trim().length > 0 && !savingStation;

        return (
            <div
                className="modal d-block"
                tabIndex="-1"
                role="dialog"
                onClick={this.closeEditStationModal}
                style={{
                    background: "rgba(15, 23, 42, 0.55)",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)"
                }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: 520 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        className="modal-content border-0 overflow-hidden d-flex flex-column"
                        style={{ borderRadius: 16, boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)" }}
                    >
                        <div
                            className="modal-header border-0 d-flex align-items-center py-2 px-3 flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #193e6c 55%, #2a5a94 100%)", color: "white" }}
                        >
                            <div className="d-flex align-items-center gap-2 me-auto">
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.14)", fontSize: 14, flexShrink: 0 }}
                                    aria-hidden="true"
                                >
                                    ✏️
                                </div>
                                <h6 className="modal-title mb-0 fw-semibold" style={{ fontSize: 15 }}>
                                    Edit Station
                                </h6>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                onClick={this.closeEditStationModal}
                                aria-label="Close"
                                disabled={savingStation}
                            />
                        </div>

                        <div className="modal-body p-3 bg-white">
                            <div className="mb-2">
                                <label className="form-label text-muted small mb-1">Station Name</label>
                                <input
                                    className="form-control rounded-3 border border-2"
                                    type="text"
                                    value={editStationName}
                                    onChange={(e) => this.setState({ editStationName: e.target.value })}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && canSave) this.saveEditStationModal();
                                        if (e.key === "Escape") this.closeEditStationModal();
                                    }}
                                    style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                />
                            </div>

                            <div className="mb-1">
                                <label className="form-label text-muted small mb-1">Zone</label>
                                <select
                                    className="form-select rounded-3 border border-2"
                                    value={editStationZone}
                                    onChange={(e) => this.setState({ editStationZone: e.target.value })}
                                    style={{ borderColor: "#aab6c4", fontSize: 14, boxShadow: "none" }}
                                >
                                    {zones.map((zone) => (
                                        <option key={zone.id} value={zone.name}>{zone.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div
                            className="modal-footer border-top border-2 bg-white py-2 px-3 d-flex justify-content-end gap-2"
                            style={{ borderColor: "#b8c2cf" }}
                        >
                            <button
                                type="button"
                                className="btn fw-semibold rounded-3 bg-light border border-2 text-secondary px-3 py-2"
                                onClick={this.closeEditStationModal}
                                disabled={savingStation}
                                style={{ fontSize: 13.5, borderColor: "#b8c2cf" }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn text-white fw-semibold rounded-3 border-0 px-3 py-2"
                                onClick={this.saveEditStationModal}
                                disabled={!canSave}
                                style={{
                                    fontSize: 13.5,
                                    background: !canSave
                                        ? "#94a3b8"
                                        : "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)",
                                    opacity: !canSave ? 0.75 : 1
                                }}
                            >
                                {savingStation ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { activeTab } = this.state;

        return (
            <>
                <Header />
                <main className="container py-4" style={{ maxWidth: 1000 }}>
                    <div className="d-flex align-items-center mb-3">
                        <div
                            className="d-flex align-items-center justify-content-center me-3 flex-shrink-0 text-white"
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                background: "linear-gradient(135deg, #1e3a5f 0%, #193e6c 100%)",
                                fontSize: 20
                            }}
                            aria-hidden="true"
                        >
                            ⚙️
                        </div>
                        <div>
                            <h4 className="mb-0 fw-semibold text-dark">Super Admin Console</h4>
                            <div className="text-muted small">Manage users, roles and stations</div>
                        </div>
                    </div>

                    {this.renderTabs()}

                    <div key={activeTab} className="tab-pane-anim">
                        {activeTab === "users"
                            ? this.renderUsersTab()
                            : activeTab === "stations"
                                ? this.renderStationsTab()
                                : activeTab === "zones"
                                    ? this.renderZonesTab()
                                    : this.renderDeveloperOptionsTab()}
                    </div>

                    <style>{`
                        .min-w-0 { min-width: 0; }
                        .tab-pane-anim { animation: tabFadeIn 260ms cubic-bezier(0.22, 1, 0.36, 1); }
                        @keyframes tabFadeIn {
                            from { opacity: 0; transform: translateY(6px); }
                            to   { opacity: 1; transform: translateY(0); }
                        }
                        @media (prefers-reduced-motion: reduce) {
                            .tab-pane-anim { animation: none !important; }
                        }
                    `}</style>
                </main>

                {this.renderCreateUserModal()}
                {this.renderEditUserModal()}
                {this.renderEditStationModal()}
            </>
        );
    }
}

export default AdminScreen;